// Versão: 1.00
// Data..: 2026-09-26
// Geração de relatórios financeiros (GestãoClick) em PDF client-side com jsPDF.
// Reaproveita o estilo do relatório gerencial (cabeçalho, KPIs e tabelas).

import { jsPDF } from 'jspdf';
import {
  FinanceStatusLabels,
  type FinanceEntryDto,
  type FinanceInvoicesReport,
  type FinanceReport,
  type FinanceSalesReport,
} from '@makweb/shared';

// Paleta alinhada à identidade do portal.
const BRAND: [number, number, number] = [0, 136, 204];
const DARK: [number, number, number] = [15, 23, 42];
const MUTED: [number, number, number] = [100, 116, 139];
const LIGHT: [number, number, number] = [241, 245, 249];

// Formata número como moeda BRL.
function brl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// Formata AAAA-MM-DD como dd/mm/aaaa (sem fuso).
function dateBr(iso?: string): string {
  if (!iso) return '-';
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}

// Contexto de desenho compartilhado entre os helpers.
interface Ctx {
  doc: jsPDF;
  pageW: number;
  pageH: number;
  margin: number;
  y: number;
}

// Desenha o cabeçalho padrão e devolve o contexto com y já avançado.
function header(title: string, subtitle: string): Ctx {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;
  let y = margin;

  doc.setFillColor(...BRAND);
  doc.rect(0, 0, pageW, 6, 'F');
  doc.setTextColor(...DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(title, margin, (y += 24));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text('Makweb Soluções — Portal de Chamados', margin, (y += 16));
  doc.text(subtitle, margin, (y += 14));
  doc.text(`Emitido em ${new Date().toLocaleString('pt-BR')}`, margin, (y += 14));
  y += 10;
  return { doc, pageW, pageH, margin, y };
}

// Desenha uma grade de KPIs (rótulo + valor).
function kpis(ctx: Ctx, items: { label: string; value: string }[]): void {
  const { doc, pageW, margin } = ctx;
  const cols = 3;
  const gap = 12;
  const cardW = (pageW - margin * 2 - gap * (cols - 1)) / cols;
  const cardH = 56;
  items.forEach((k, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const x = margin + col * (cardW + gap);
    const cy = ctx.y + row * (cardH + gap);
    doc.setFillColor(...LIGHT);
    doc.roundedRect(x, cy, cardW, cardH, 6, 6, 'F');
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.text(k.label.toUpperCase(), x + 12, cy + 18);
    doc.setTextColor(...DARK);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text(k.value, x + 12, cy + 42);
  });
  ctx.y += Math.ceil(items.length / cols) * (cardH + gap) + 8;
}

// Desenha uma tabela com colunas alinhadas (larguras relativas somando 1).
function table(ctx: Ctx, title: string, columns: { header: string; width: number; align?: 'left' | 'right' }[], rows: string[][]): void {
  const { doc, pageW, pageH, margin } = ctx;
  if (ctx.y > pageH - 120) { doc.addPage(); ctx.y = margin; }
  doc.setTextColor(...BRAND);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(title, margin, (ctx.y += 18));
  ctx.y += 6;

  const tableW = pageW - margin * 2;
  const colX = (idx: number) => margin + columns.slice(0, idx).reduce((s, c) => s + c.width, 0) * tableW;

  // Cabeçalho da tabela.
  doc.setFillColor(...BRAND);
  doc.rect(margin, ctx.y, tableW, 20, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  columns.forEach((c, i) => {
    const x = c.align === 'right' ? colX(i) + c.width * tableW - 8 : colX(i) + 8;
    doc.text(c.header, x, ctx.y + 14, { align: c.align ?? 'left' });
  });
  ctx.y += 20;

  if (rows.length === 0) {
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.text('Sem dados no período.', margin + 8, (ctx.y += 16));
    ctx.y += 6;
    return;
  }

  const rowH = 18;
  rows.forEach((r, ri) => {
    if (ctx.y > pageH - 60) {
      doc.addPage();
      ctx.y = margin;
    }
    if (ri % 2 === 0) {
      doc.setFillColor(248, 250, 252);
      doc.rect(margin, ctx.y, tableW, rowH, 'F');
    }
    doc.setTextColor(...DARK);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    columns.forEach((c, ci) => {
      const x = c.align === 'right' ? colX(ci) + c.width * tableW - 8 : colX(ci) + 8;
      const text = doc.splitTextToSize(r[ci] ?? '', c.width * tableW - 12)[0] ?? '';
      doc.text(String(text), x, ctx.y + 12, { align: c.align ?? 'left' });
    });
    ctx.y += rowH;
  });
  ctx.y += 8;
}

// Rodapé confidencial em todas as páginas + salva o arquivo.
function finish(ctx: Ctx, fileName: string): void {
  const { doc, pageW, pageH, margin } = ctx;
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`Página ${p} de ${pages}`, pageW - margin, pageH - 20, { align: 'right' });
    doc.text('Makweb Soluções — confidencial (uso administrativo)', margin, pageH - 20);
  }
  doc.save(fileName);
}

// Limita o nº de linhas detalhadas para o PDF não ficar gigante.
const MAX_ROWS = 200;

function entryRows(entries: FinanceEntryDto[]): string[][] {
  return entries.slice(0, MAX_ROWS).map((e) => [
    e.companyName ?? e.clientName ?? '-',
    e.description ?? e.code ?? '-',
    dateBr(e.dueDate),
    FinanceStatusLabels[e.status] ?? e.status,
    e.daysOverdue > 0 ? String(e.daysOverdue) : '-',
    brl(e.amount),
  ]);
}

// ---- Relatório de contas a receber/pagar ----
export function generateFinanceReportPdf(report: FinanceReport): void {
  const periodo = `Período: ${dateBr(report.from) } a ${dateBr(report.to)}`;
  const ctx = header('Relatório Financeiro', periodo);
  const r = report.receivables.totals;
  const p = report.payables.totals;
  kpis(ctx, [
    { label: 'A receber (aberto)', value: brl(r.openAmount) },
    { label: 'A receber (atrasado)', value: brl(r.overdueAmount) },
    { label: 'Recebido', value: brl(r.paidAmount) },
    { label: 'A pagar (aberto)', value: brl(p.openAmount) },
    { label: 'A pagar (atrasado)', value: brl(p.overdueAmount) },
    { label: 'Pago', value: brl(p.paidAmount) },
  ]);

  const cols = [
    { header: 'Cliente', width: 0.26, align: 'left' as const },
    { header: 'Descrição', width: 0.24, align: 'left' as const },
    { header: 'Vencimento', width: 0.14, align: 'left' as const },
    { header: 'Situação', width: 0.12, align: 'left' as const },
    { header: 'Atraso', width: 0.08, align: 'right' as const },
    { header: 'Valor', width: 0.16, align: 'right' as const },
  ];
  table(ctx, 'Contas a receber', cols, entryRows(report.receivables.entries));
  table(ctx, 'Contas a pagar', cols, entryRows(report.payables.entries));

  if (report.projection.length) {
    table(
      ctx,
      'Projeção de recebimentos (em aberto por mês)',
      [
        { header: 'Mês', width: 0.5, align: 'left' },
        { header: 'Lançamentos', width: 0.25, align: 'right' },
        { header: 'Total previsto', width: 0.25, align: 'right' },
      ],
      report.projection.map((pt) => [pt.month, String(pt.count), brl(pt.amount)]),
    );
  }
  finish(ctx, `financeiro-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ---- Relatório de vendas de serviço ----
export function generateSalesReportPdf(report: FinanceSalesReport): void {
  const periodo = `Período: ${dateBr(report.from)} a ${dateBr(report.to)}`;
  const ctx = header('Relatório de Vendas (Serviços)', periodo);
  kpis(ctx, [
    { label: 'Total do período', value: brl(report.total) },
    { label: 'Nº de vendas', value: String(report.count) },
    { label: 'Ticket médio', value: brl(report.count ? report.total / report.count : 0) },
  ]);
  table(
    ctx,
    'Vendas por mês',
    [
      { header: 'Mês', width: 0.5, align: 'left' },
      { header: 'Vendas', width: 0.25, align: 'right' },
      { header: 'Total', width: 0.25, align: 'right' },
    ],
    report.byMonth.map((m) => [m.month, String(m.count), brl(m.total)]),
  );
  finish(ctx, `vendas-servicos-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ---- Relatório de notas fiscais de serviço ----
export function generateInvoicesReportPdf(report: FinanceInvoicesReport): void {
  const ctx = header('Notas Fiscais de Serviço', `Total de ${report.count} nota(s)`);
  kpis(ctx, [
    { label: 'Qtd. de notas', value: String(report.count) },
    { label: 'Valor total', value: brl(report.total) },
    { label: 'Situações', value: String(Object.keys(report.byStatus).length) },
  ]);
  table(
    ctx,
    'Notas por situação',
    [
      { header: 'Situação', width: 0.6, align: 'left' },
      { header: 'Quantidade', width: 0.4, align: 'right' },
    ],
    Object.entries(report.byStatus).map(([s, c]) => [s, String(c)]),
  );
  table(
    ctx,
    'Notas fiscais',
    [
      { header: 'Número', width: 0.12, align: 'left' },
      { header: 'Emissão', width: 0.14, align: 'left' },
      { header: 'Cliente', width: 0.32, align: 'left' },
      { header: 'Situação', width: 0.16, align: 'left' },
      { header: 'ISS', width: 0.12, align: 'right' },
      { header: 'Total', width: 0.14, align: 'right' },
    ],
    report.invoices.slice(0, MAX_ROWS).map((n) => [
      n.number ?? '-',
      dateBr(n.issueDate),
      n.clientName ?? '-',
      n.status ?? '-',
      brl(n.issAmount),
      brl(n.totalAmount),
    ]),
  );
  finish(ctx, `notas-fiscais-${new Date().toISOString().slice(0, 10)}.pdf`);
}
